<template>
  <div class="ssb" @click="openFilter = null">
    <div class="ssb__toolbar">
      <label class="ssb__field ssb__field--search">
        Buscar paciente
        <input
          v-model="patientSearch"
          type="text"
          class="form-control form-control-sm"
          placeholder="Nome, documento ou telefone"
          autocomplete="off"
        >
      </label>
      <label class="ssb__field">
        Início
        <input v-model="dateStart" type="date" class="form-control form-control-sm">
      </label>
      <label class="ssb__field">
        Fim
        <input v-model="dateEnd" type="date" class="form-control form-control-sm">
      </label>
      <div class="ssb__field">
        Agrupar por
        <div class="sw-chip-group ssb__group">
          <button
            type="button"
            class="sw-chip"
            :class="{ 'is-active': groupBy === 'professional' }"
            @click="setGroupBy('professional')"
          >
            Profissional
          </button>
          <button
            type="button"
            class="sw-chip"
            :class="{ 'is-active': groupBy === 'specialty' }"
            @click="setGroupBy('specialty')"
          >
            Especialidade
          </button>
        </div>
      </div>
      <div class="ssb__actions">
        <button
          type="button"
          class="sw-chip sw-chip--primary"
          :disabled="loading"
          @click="loadBoard()"
        >
          Atualizar
        </button>
        <div class="sw-chip-group ssb__modes">
          <button type="button" class="sw-chip" @click="$emit('change-mode', 'agenda')">
            Agenda
          </button>
          <button type="button" class="sw-chip" @click="$emit('change-mode', 'reception')">
            Recepção
          </button>
          <button type="button" class="sw-chip is-active" title="Recepção Multiprofissional">
            Recepção Multiprofissional
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="ssb__state">
      <span class="spinner-border text-primary"></span>
    </div>

    <div v-else-if="columns.length === 0" class="ssb__state">
      <p class="ssb__empty">Nenhum agendamento encontrado para o período e filtros atuais.</p>
    </div>

    <div v-else-if="!hasVisibleColumns" class="ssb__state">
      <p class="ssb__empty">Nenhum paciente encontrado para “{{ patientSearch.trim() }}”.</p>
    </div>

    <draggable
      v-else
      v-model="columns"
      item-key="key"
      class="ssb__scroll"
      handle=".ssb__col-drag"
      :animation="180"
      :disabled="Boolean(patientSearch.trim())"
      @end="persistColumnOrder"
    >
      <template #item="{ element: col }">
        <section
          v-show="isColumnVisible(col)"
          class="ssb__col"
        >
          <div class="ssb__col-head">
            <button
              type="button"
              class="ssb__col-drag"
              title="Arrastar para reordenar"
              :disabled="Boolean(patientSearch.trim())"
              @click.stop
            >
              <i class="bi bi-grip-vertical"></i>
            </button>
            <h3>{{ col.label }}</h3>
            <span class="sw-count">{{ filteredSchedules(col).length }}</span>
          </div>
          <ScheduleFilterSelect
            v-if="groupBy === 'specialty'"
            :model-value="columnFilter[col.key]"
            :options="col.professionals || []"
            option-label="prof_name"
            option-value="id_prof"
            placeholder="Todos os profissionais"
            searchable
            show-clear
            :open="openFilter === col.key"
            @update:open="setOpenFilter(col.key, $event)"
            @update:modelValue="setColumnFilter(col.key, $event)"
          />
          <ScheduleFilterSelect
            v-else
            :model-value="columnFilter[col.key]"
            :options="col.specialties || []"
            option-label="spec_name"
            option-value="id_spec"
            placeholder="Todas as especialidades"
            searchable
            show-clear
            :open="openFilter === col.key"
            @update:open="setOpenFilter(col.key, $event)"
            @update:modelValue="setColumnFilter(col.key, $event)"
          />
          <div class="ssb__col-body">
            <ScheduleAppointmentCard
              v-for="sch in filteredSchedules(col)"
              :key="sch.id_sched"
              class="mb-2"
              :schedule="sch"
              variant="reception"
              show-time
              :show-prof="groupBy === 'specialty'"
              :show-spec="groupBy === 'professional'"
              @click="$emit('openSchedule', $event)"
              @takePicture="onTakePicture"
              @openRecurrence="$emit('openRecurrence', $event)"
              @onArriveSchedule="applyArrive"
              @onFinishSchedule="applyFinish"
              @onReopenSchedule="applyReopen"
              @onStartAttendance="applyStart"
            />
            <p v-if="!filteredSchedules(col).length" class="ssb__col-empty">Nenhum paciente</p>
          </div>
        </section>
      </template>
    </draggable>

    <ModalPhoto
      :openModal="openModalPhoto"
      :initialPhoto="scheduleSelected?.patient_photo ? scheduleSelected?.patient_photo : null"
      @onSaveClick="savePhoto"
      @onClearClick="savePhoto"
      @onClose="openModalPhoto = false"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import http from '../../helpers/http';
import moment from '../../helpers/date';
import Alert from '../../helpers/alert';
import str from '../../helpers/str';
import {
  loadScheduleWorkspacePrefs,
  saveScheduleWorkspacePrefs,
  sortItemsByIdOrder,
  mergeColumnOrder,
} from '../../helpers/scheduleWorkspacePrefs';
import ModalPhoto from '../../components/ModalPhoto.vue';
import ScheduleAppointmentCard from './workspace/ScheduleAppointmentCard.vue';
import ScheduleFilterSelect from './workspace/ScheduleFilterSelect.vue';
import './workspace/scheduleWorkspace.css';

export default {
  name: 'SchedulesSpecialtyBoard',
  components: {
    ModalPhoto,
    ScheduleAppointmentCard,
    ScheduleFilterSelect,
    draggable,
  },
  emits: ['openSchedule', 'openRecurrence', 'refresh', 'change-mode'],
  data() {
    const today = moment().format('YYYY-MM-DD');
    const prefs = loadScheduleWorkspacePrefs({
      userId: this.$store.state.auth.user?.id_user,
      placeId: this.$store.state.auth.place?.id_place,
    });
    return {
      columns: [],
      loading: false,
      dateStart: today,
      dateEnd: today,
      patientSearch: '',
      groupBy: prefs.groupBy || 'professional',
      columnOrder: prefs.columnOrder || { professional: [], specialty: [] },
      columnFilter: {},
      openFilter: null,
      openModalPhoto: false,
      scheduleSelected: null,
      echoProfIds: [],
      skipRealtimeUntil: 0,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    prefsScope() {
      return {
        userId: this.$store.state.auth.user?.id_user,
        placeId: this.$store.state.auth.place?.id_place,
      };
    },
    columnIdKey() {
      return this.groupBy === 'specialty' ? 'id_spec' : 'id_prof';
    },
    patientSearchTerm() {
      return this.patientSearch.trim();
    },
    hasVisibleColumns() {
      return this.columns.some((col) => this.isColumnVisible(col));
    },
  },
  watch: {
    dateStart() {
      this.loadBoard();
    },
    dateEnd() {
      this.loadBoard();
    },
  },
  mounted() {
    this.loadBoard();
  },
  beforeUnmount() {
    this.teardownEcho();
  },
  methods: {
    setGroupBy(value) {
      if (this.groupBy === value) return;
      this.groupBy = value;
      this.columnFilter = {};
      this.openFilter = null;
      saveScheduleWorkspacePrefs({ groupBy: this.groupBy }, this.prefsScope);
      this.loadBoard();
    },
    setOpenFilter(key, isOpen) {
      this.openFilter = isOpen ? key : (this.openFilter === key ? null : this.openFilter);
    },
    setColumnFilter(key, value) {
      this.columnFilter = { ...this.columnFilter, [key]: value };
    },
    matchesPatientSearch(schedule) {
      const term = this.patientSearchTerm;
      if (!term) return true;

      const termLower = term.toLowerCase();
      const name = String(schedule?.patient_name || '').toLowerCase();
      if (name.includes(termLower) || String(schedule?.document_number || '').toLowerCase().includes(termLower)) return true;

      const digits = term.replace(/\D/g, '');
      if (!digits) return false;

      const cpf = String(schedule?.patient_cpf || '').replace(/\D/g, '');
      const phone = String(schedule?.patient_phone || '').replace(/\D/g, '');
      return cpf.includes(digits) || phone.includes(digits);
    },
    filteredSchedules(col) {
      const filterId = this.columnFilter[col.key];
      return (col.schedules || []).filter((item) => {
        if (!this.matchesPatientSearch(item)) return false;
        if (filterId == null || filterId === '') return true;
        if (this.groupBy === 'specialty') {
          return Number(item.id_prof) === Number(filterId);
        }
        return Number(item.id_spec) === Number(filterId);
      });
    },
    isColumnVisible(col) {
      if (!this.patientSearchTerm) return true;
      return this.filteredSchedules(col).length > 0;
    },
    applySavedColumnOrder(columns) {
      const order = this.columnOrder?.[this.groupBy] || [];
      return sortItemsByIdOrder(columns, order, this.columnIdKey);
    },
    persistColumnOrder() {
      const currentIds = this.columns.map((col) => Number(col[this.columnIdKey]));
      const previousIds = this.columnOrder?.[this.groupBy] || [];
      const nextIds = mergeColumnOrder(previousIds, currentIds);
      this.columnOrder = {
        ...this.columnOrder,
        [this.groupBy]: nextIds,
      };
      saveScheduleWorkspacePrefs({ columnOrder: this.columnOrder }, this.prefsScope);
    },
    async loadBoard({ silent = false } = {}) {
      if (!this.place?.id_place) return;
      if (!silent) this.loading = true;
      try {
        const res = await http.get(`/places/${this.place.id_place}/schedules/board`, {
          params: {
            start_date: this.dateStart,
            end_date: this.dateEnd,
            group_by: this.groupBy,
          },
        });
        const nextColumns = this.applySavedColumnOrder(res.data.columns || []);
        this.columns = nextColumns;
        const nextFilter = { ...this.columnFilter };
        for (const col of nextColumns) {
          if (nextFilter[col.key] === undefined) nextFilter[col.key] = null;
        }
        this.columnFilter = nextFilter;
        this.syncEcho();
      } catch (e) {
        console.error(e);
        Alert.toastError(e.response?.data?.message || 'Erro ao carregar quadro de agenda.');
      } finally {
        this.loading = false;
      }
    },
    syncEcho() {
      this.teardownEcho();
      const echo = this.echoClient();
      if (!echo) return;
      const ids = new Set();
      for (const col of this.columns) {
        for (const item of col.schedules || []) {
          if (item.id_prof) ids.add(Number(item.id_prof));
        }
      }
      for (const id of ids) {
        echo.private(`professionals.${id}.queues`)
          .listen('ScheduleArrived', this.onRealtime)
          .listen('ScheduleStarted', this.onRealtime)
          .listen('ScheduleFinished', this.onRealtime)
          .listen('ScheduleNotArrived', this.onRealtime);
        this.echoProfIds.push(id);
      }
    },
    teardownEcho() {
      const echo = this.echoClient();
      if (echo) {
        for (const id of this.echoProfIds) {
          echo.leave(`professionals.${id}.queues`);
        }
      }
      this.echoProfIds = [];
    },
    echoClient() {
      if (typeof Echo !== 'undefined' && Echo) return Echo;
      return window.Echo || null;
    },
    onRealtime() {
      if (Date.now() < this.skipRealtimeUntil) return;
      this.loadBoard({ silent: true });
    },
    findSchedule(id) {
      for (const col of this.columns) {
        const item = (col.schedules || []).find((row) => String(row.id_sched) === String(id));
        if (item) return item;
      }
      return null;
    },
    stamp() {
      return moment().format('YYYY-MM-DD HH:mm:ss');
    },
    touchSchedule() {
      this.skipRealtimeUntil = Date.now() + 2500;
    },
    applyArrive(id) {
      const item = this.findSchedule(id);
      if (!item) return;
      this.touchSchedule();
      item.sched_arrive = item.sched_arrive ? null : this.stamp();
    },
    applyStart(id) {
      const item = this.findSchedule(id);
      if (!item || item.sched_start) return;
      this.touchSchedule();
      item.sched_start = this.stamp();
    },
    applyFinish(id) {
      const item = this.findSchedule(id);
      if (!item) return;
      this.touchSchedule();
      item.sched_status = 'FI';
      item.is_sched_finished = true;
      item.sched_end = this.stamp();
    },
    applyReopen(id) {
      const item = this.findSchedule(id);
      if (!item) return;
      this.touchSchedule();
      item.sched_status = item.confirmed_at ? 'CO' : 'AG';
      item.is_sched_finished = false;
      item.sched_end = null;
    },
    onTakePicture(schedule) {
      this.scheduleSelected = schedule;
      this.openModalPhoto = true;
    },
    savePhoto(photo) {
      this.openModalPhoto = false;
      if (!this.scheduleSelected) return;
      const formData = new FormData();
      formData.append('_method', 'put');
      if (photo) {
        formData.append('patient_photo', str.convertFileToBase64(photo.split(',')[1]));
      }
      http({
        method: 'post',
        url: `/places/${this.place.id_place}/patients/${this.scheduleSelected.id_patient}`,
        data: formData,
      }).then(() => {
        this.scheduleSelected = null;
        this.$emit('refresh');
        Alert.toastSuccess('Foto armazenada com sucesso!');
      }).catch((e) => {
        Alert.toastError(e.response?.data);
      });
    },
  },
};
</script>

<style scoped>
.ssb {
  --sw-brand: #780f5a;
  --sw-line: #e4e6ef;
  --sw-muted: #7e8299;
  --sw-ink: #181c32;
  --sw-soft: #f8f5f7;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 0 1rem 0.75rem;
  overflow: hidden;
}
.ssb__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid var(--sw-line);
  border-radius: 0.85rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.85rem;
}
.ssb__actions {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  flex-shrink: 0;
}
.ssb__actions .sw-chip {
  min-height: 2.4rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.92rem;
}
.ssb__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #7e8299;
}
.ssb__field--search {
  flex: 0 1 14rem;
  min-width: 10rem;
}
.ssb__field .form-control {
  min-height: 2.15rem;
}
.ssb .ssb__group .sw-chip {
  min-height: 2.15rem;
  padding: 0.38rem 0.85rem;
  background: #fff;
  border-color: #e4e6ef;
  color: #5e6278;
}
.ssb .ssb__group .sw-chip:hover {
  border-color: #780f5a;
  color: #780f5a;
  background: #fbf7f9;
}
.ssb .ssb__group .sw-chip.is-active {
  background: #f6eaf1;
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: inset 0 0 0 1px #780f5a;
}
.ssb__state {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}
.ssb__empty {
  margin: 0;
  padding: 1.4rem 1rem;
  color: #a1a5b7;
  font-size: 0.86rem;
  font-weight: 600;
  text-align: center;
  background: #fafbfc;
  border: 1px dashed #e4e6ef;
  border-radius: 0.7rem;
}
.ssb__scroll {
  display: flex;
  flex: 1 1 auto;
  gap: 1rem;
  min-width: 0;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 0.4rem;
  scrollbar-width: thin;
  scrollbar-color: #d8dbe3 transparent;
}
.ssb__scroll::-webkit-scrollbar {
  height: 0.45rem;
}
.ssb__scroll::-webkit-scrollbar-thumb {
  background: #d8dbe3;
  border-radius: 999px;
}
.ssb__col {
  display: flex;
  flex-direction: column;
  flex: 1 0 300px;
  min-width: 300px;
  max-width: 420px;
  min-height: 0;
  padding: 0.75rem;
  background: #f9f9fb;
  border-radius: 0.65rem;
}
.ssb__col-head {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.55rem;
}
.ssb__col-drag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: 0;
  border-radius: 0.35rem;
  background: transparent;
  color: #a1a5b7;
  cursor: grab;
  flex-shrink: 0;
}
.ssb__col-drag:hover:not(:disabled) {
  color: #780f5a;
  background: #f6eaf1;
}
.ssb__col-drag:disabled {
  cursor: default;
  opacity: 0.45;
}
.ssb__col-drag:active:not(:disabled) {
  cursor: grabbing;
}
.ssb__col-head h3 {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.88rem;
  font-weight: 800;
  color: #181c32;
}
.ssb__col-body {
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 0.65rem;
  overflow-x: hidden;
  overflow-y: auto;
}
.ssb__col-empty {
  margin: 0.6rem 0 0;
  color: #a1a5b7;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
}
</style>
